import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useDiscardChangesWarning } from '../../../lib/hooks/use-discard-changes-warning.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { SalesDraftDetailOutput, SalesDraftType } from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import type { DialogInnerProps } from '../../dialog/types.ts';
import type { DraftOrderFormProps } from './draft-order-form.tsx';
import { DraftOrderForm } from './draft-order-form.tsx';
import { ErpConnectionErrorBanner } from './erp-connection-error-banner.tsx';

type DraftOrderCreateDialogProps = {
    onSuccess: (data: SalesDraftDetailOutput) => void;
    defaultSelectedDraftType?: SalesDraftType;
} & DraftOrderFormProps &
    DialogInnerProps;

export const DraftOrderCreateDialog = DialogStateManager.create(
    ({ customerId, storageId, onSuccess, defaultSelectedDraftType }: DraftOrderCreateDialogProps) => {
        const { transactionDraftAvailability } = useAppConfig();
        const isErpUnreachable = transactionDraftAvailability === 'CURRENTLY_UNAVAILABLE';
        const dialog = useDialog();
        const { t } = useLocale();
        const { toast } = useToast();

        const sendEvent = useAnalytics();

        const [isDirty, setIsDirty] = useState(false);
        const { confirmDiscard } = useDiscardChangesWarning(isDirty);

        const handleClose = (): void => {
            confirmDiscard(dialog.remove);
        };

        useEffect(() => {
            sendEvent('transaction_creation_started', { customer_id: customerId });
        }, [sendEvent, customerId]);

        const { mutate, isPending } = api.platoUser.command.salesDraft.draft.create.useMutation({
            onSuccess(data) {
                onSuccess(data);
                dialog.remove();
            },
            onError(error) {
                console.error('onError', error);

                toast({
                    title: 'error',
                    description: error.message,
                    icon: 'warning',
                });
            },
        });

        return (
            <Dialog open={dialog.visible} onClose={handleClose} showClose>
                <DialogForm className="max-w-2xl">
                    <DialogForm.Header title={t('sales_draft__new_form___title')} />
                    {isErpUnreachable && (
                        <div className="px-4 py-3">
                            <ErpConnectionErrorBanner />
                        </div>
                    )}
                    <DraftOrderForm
                        customerId={customerId}
                        storageId={storageId}
                        onSubmit={(data) => {
                            mutate({
                                draft_order_id: uuidv4(),
                                ...data,
                            });
                        }}
                        onCancel={handleClose}
                        onDirtyChange={setIsDirty}
                        isLoading={isPending}
                        isDisabled={isErpUnreachable}
                        defaultSelectedDraftType={defaultSelectedDraftType}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);
